/** Recording Studio ↔ shared engine: which channel holds which session track, and loading takes onto them. */
import { trimHead } from "@/lib/creatorVocal";
import { absUrl } from "@/lib/recStudioApi";
import { playableUrl } from "@/lib/creatorApi";
import { applyFx } from "@/lib/vocalPresets";
import { blendStack } from "@/components/recstudio/takeRender";

const channelMap = new Map(); // "<sessionId>:<trackId|instrumental>" → channel index (survives route changes)
const takeBuffers = new Map(); // take file_url → aligned AudioBuffer (trimmed + start padding)

export const INSTRUMENTAL = "instrumental";
const keyOf = (sid, part) => `${sid}:${part}`;

/** The channel owned by this session part, or null. */
export function channelOf(engine, sid, part) {
  const idx = channelMap.get(keyOf(sid, part));
  return idx != null && engine.channels[idx]?.recOwner === keyOf(sid, part) ? idx : null;
}

function claim(engine, sid, part) {
  const have = channelOf(engine, sid, part);
  if (have != null) return have;
  const idx = engine.channels.findIndex((c) => !c.buffer && !c.recOwner);
  if (idx < 0) throw new Error("All 64 studio channels are full — clear one in the Advanced Studio first.");
  engine.channels[idx].recOwner = keyOf(sid, part);
  channelMap.set(keyOf(sid, part), idx);
  return idx;
}

/** Reserve (or reuse) the channel a part records / plays on. */
export const claimChannel = claim;

/** Take over a channel that already holds the right audio (e.g. a track loaded in the Advanced Studio). */
export function adoptChannel(engine, sid, part, idx, recKey) {
  Object.assign(engine.channels[idx], { recOwner: keyOf(sid, part), recKey });
  channelMap.set(keyOf(sid, part), idx);
}

/** Put a buffer on the part's channel and mark it as ours. */
export function placeBuffer(engine, sid, part, buffer, name, recKey) {
  const idx = claim(engine, sid, part);
  engine.loadBufferToChannel(idx, buffer, name, recKey?.startsWith("https://") ? recKey : null);
  Object.assign(engine.channels[idx], { recOwner: keyOf(sid, part), recKey });
  return idx;
}

export function releasePart(engine, sid, part) {
  const idx = channelOf(engine, sid, part);
  if (idx != null) {
    engine.clearChannel(idx);
    Object.assign(engine.channels[idx], { recOwner: null, recKey: null });
  }
  channelMap.delete(keyOf(sid, part));
}

/** Fetch + decode without waiting on ctx.resume() (Chrome holds that until a click, which would stall page load). */
async function fetchBuffer(engine, url) {
  const u = playableUrl(absUrl(url));
  const own = u.startsWith(process.env.REACT_APP_BACKEND_URL);
  const res = await fetch(u, own ? { credentials: "include" } : undefined);
  if (!res.ok) throw new Error(`Couldn't load the audio (${res.status})`);
  return engine.ctx.decodeAudioData(await res.arrayBuffer());
}

/** Prepend `sec` of silence so a take recorded from mid-song lines up. */
export function padStart(ctx, buffer, sec) {
  const pad = Math.round(Math.max(0, sec) * buffer.sampleRate);
  if (!pad) return buffer;
  const out = ctx.createBuffer(buffer.numberOfChannels, buffer.length + pad, buffer.sampleRate);
  for (let c = 0; c < buffer.numberOfChannels; c += 1) out.copyToChannel(buffer.getChannelData(c), c, pad);
  return out;
}

export const alignTake = (ctx, raw, trimSec, startSec) => padStart(ctx, trimHead(ctx, raw, trimSec), startSec);
export const rememberTake = (url, buffer) => takeBuffers.set(url, buffer);

/** Takes in the track's choir stack (only when stacking is on). */
export const stackTakes = (t) => (t.stack?.on ? t.stack.take_ids.map((id) => t.takes.find((x) => x.id === id)).filter(Boolean) : []);

/** Decoded, aligned audio for a saved take (cached). */
export async function takeBuffer(engine, take) {
  if (takeBuffers.has(take.file_url)) return takeBuffers.get(take.file_url);
  const raw = await fetchBuffer(engine, take.file_url).catch((e) => { throw new Error(`${take.name}: ${e.message}`); });
  const buf = alignTake(engine.ctx, raw, take.trim_head_sec || 0, take.start_sec || 0);
  takeBuffers.set(take.file_url, buf);
  return buf;
}

/** Make the engine match the session: instrumental + each track's chosen take, volumes, mute / solo, vocal sound. */
export async function syncSession(engine, doc) {
  const sid = doc.id;
  const inst = doc.instrumental;
  if (inst) {
    const idx = channelOf(engine, sid, INSTRUMENTAL);
    if (idx == null || engine.channels[idx].recKey !== inst.url) placeBuffer(engine, sid, INSTRUMENTAL, await fetchBuffer(engine, inst.url), `Instrumental · ${inst.name}`, inst.url);
  } else releasePart(engine, sid, INSTRUMENTAL);
  for (const t of doc.tracks) {
    const idx = channelOf(engine, sid, t.id);
    const stack = stackTakes(t);
    if (stack.length) {
      const key = `stack:${stack.map((x) => x.id).join(",")}:${t.stack.spread}`;
      if (idx == null || engine.channels[idx].recKey !== key) {
        const bufs = await Promise.all(stack.map((x) => takeBuffer(engine, x)));
        placeBuffer(engine, sid, t.id, blendStack(engine.ctx, bufs, t.stack.spread), t.name, key);
      }
      continue;
    }
    const take = t.takes.find((x) => x.id === t.active_take_id);
    if (!take) { if (idx != null && engine.channels[idx].buffer) engine.clearChannel(idx); continue; }
    if (idx == null || engine.channels[idx].recKey !== take.file_url) placeBuffer(engine, sid, t.id, await takeBuffer(engine, take), t.name, take.file_url);
  }
  return applyMix(engine, doc);
}

/** Volumes, mute / solo and vocal sounds only — instant, no loading. */
export function applyMix(engine, doc) {
  const sid = doc.id;
  let wet = false;
  const set = (idx, p) => {
    engine.setChannelVolume(idx, p.volume);
    engine.setChannelMute(idx, Boolean(p.muted));
    engine.setChannelSolo(idx, Boolean(p.solo));
  };
  const i = channelOf(engine, sid, INSTRUMENTAL);
  if (i != null && doc.instrumental) set(i, doc.instrumental);
  doc.tracks.forEach((t) => {
    const idx = channelOf(engine, sid, t.id);
    if (idx == null) return;
    engine.channels[idx].name = t.name;
    set(idx, t);
    wet = applyFx(engine, idx, t.fx) || wet;
  });
  return wet;
}

/** Engine channels with audio that aren't part of this session (still loaded from the Advanced Studio). */
export function otherChannels(engine, sid) {
  return engine.channels.map((c, idx) => ({ c, idx })).filter(({ c }) => c.buffer && !(c.recOwner || "").startsWith(`${sid}:`));
}

/** Every channel index this session plays on. */
export function sessionChannels(engine, doc) {
  return [INSTRUMENTAL, ...doc.tracks.map((t) => t.id)].map((p) => channelOf(engine, doc.id, p)).filter((x) => x != null && engine.channels[x].buffer);
}
